/**
 * Filter Manager - Refactored Version
 *
 * Simplified, robust filter management for Kendo Grids with automatic component detection.
 * Uses Adapter Pattern for unified component handling.
 *
 * Key improvements:
 * - Simplified initialization with better error handling
 * - Reduced complexity and redundant code
 * - Better separation of concerns
 * - Improved performance and memory management
 * - Cleaner API with consistent behavior
 */

import { CONFIG } from '../../core/config.js';
import eventBus, { EVENTS } from '../../core/event-bus.js';
import { logError, logWarning, logDebug, safeDOM } from '../../core/error-handler.js';
import { ObjectUtils, FormUtils } from '../../core/shared-utilities.js';
import { FilterComponent, FilterComponentFactory } from './components.js';
import { AdapterFactory } from './adapters/adapter-factory.js';
import { FilterStorage } from './session-storage.js';

/**
 * Filter Manager class for handling auto-filtering on grid pages
 * Uses polymorphic filter components for clean, maintainable code
 */
export class FilterManager {
    /**
     * Create FilterManager instance with simplified options
     * @param {Object|string} options Configuration options or grid selector string
     * @returns {FilterManager} New FilterManager instance
     */
    static create(options = {}) {
        return new FilterManager(options);
    }

    constructor(options = {}) {
        // Normalize options
        if (typeof options === 'string') {
            options = { gridSelector: options };
        }

        // Configuration with defaults
        this.config = {
            gridSelector: options.gridSelector || CONFIG.SELECTORS.GRID,
            filterFormSelector: options.filterFormSelector || CONFIG.SELECTORS.FILTER_FORM,
            clearButtonSelector: options.clearButtonSelector || CONFIG.SELECTORS.CLEAR_FILTERS_BTN,
            filterButtonSelector: options.filterButtonSelector || CONFIG.SELECTORS.FILTER_BTN,
            autoInitialize: options.autoInitialize !== false, // Default true
            retryAttempts: options.retryAttempts || 5,
            retryDelay: options.retryDelay || 200,
            enableSessionStorage: options.enableSessionStorage !== false, // Default true
            onFilter: options.onFilter || null // Custom callback when filter changes
        };

        // State management
        this.filterComponents = new Map(); // Use Map for better performance
        this.isInitialized = false;
        this.isDestroyed = false;
        this.retryCount = 0;

        // Bind methods to preserve context
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleClearFilters = this.handleClearFilters.bind(this);
        this.handleGridRefresh = this.handleGridRefresh.bind(this);

        // Register globally for getFilterValues function
        this.registerGlobally();

        // Auto-initialize if enabled
        if (this.config.autoInitialize) {
            this.initialize();
        }
    }

    /**
     * Initialize the filter manager
     */
    async initialize() {
        if (this.isInitialized || this.isDestroyed) {
            return;
        }

        try {
            // Wait for DOM to be ready
            await this.waitForDOM();

            // Setup event handlers
            this.setupEventHandlers();

            // Attach to grid with retry mechanism
            await this.attachToGrid();

            // Detect and initialize components
            await this.detectAndInitializeComponents();

            this.isInitialized = true;

            // Emit ready event
            eventBus.emit(EVENTS.FILTER_MANAGER_READY, { manager: this });
        } catch (error) {
            logError(error);
            this.handleInitializationError(error);
        }
    }

    /**
     * Wait for DOM to be ready
     */
    waitForDOM() {
        return new Promise(resolve => {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', resolve, { once: true });
            } else {
                resolve();
            }
        });
    }

    /**
     * Setup event handlers for filter and clear buttons
     */
    setupEventHandlers() {
        // Filter button
        const filterBtn = document.querySelector(this.config.filterButtonSelector);
        if (filterBtn) {
            filterBtn.addEventListener('click', this.handleGridRefresh);
        }

        // Clear filters button
        const clearBtn = document.querySelector(this.config.clearButtonSelector);
        if (clearBtn) {
            clearBtn.addEventListener('click', this.handleClearFilters);
        }

        // Grid events for better integration
        this.setupGridEventListeners();
    }

    /**
     * Setup grid event listeners
     */
    setupGridEventListeners() {
        const gridElement = document.querySelector(this.config.gridSelector);
        if (!gridElement) return;

        // Listen for grid ready event
        gridElement.addEventListener('kendoGridReady', () => {
            this.attachToGrid();
        });

        // Listen for data bound event
        gridElement.addEventListener('dataBound', () => {
            this.updateClearButtonState();
        });
    }

    /**
     * Attach this FilterManager instance to the grid
     */
    async attachToGrid() {
        const maxRetries = this.config.retryAttempts;
        const retryDelay = this.config.retryDelay;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const grid = $(this.config.gridSelector).data('kendoGrid');
                if (grid) {
                    grid._filterManager = this;
                    return;
                }
            } catch (error) {
                logWarning(`Attempt ${attempt} failed: ${error.message}`);
            }

            if (attempt < maxRetries) {
                await this.delay(retryDelay);
            }
        }

        throw new Error(`FilterManager: Failed to attach to grid after ${maxRetries} attempts`);
    }

    /**
     * Detect and initialize filter components
     */
    async detectAndInitializeComponents() {
        const filterForm = safeDOM(
            () => document.querySelector(this.config.filterFormSelector),
            null
        );
        if (!filterForm) {
            logWarning('No filter form found');
            return;
        }

        // Find all input and select elements with IDs (select for Kendo MultiSelect, ComboBox, etc.)
        const inputs = Array.from(filterForm.querySelectorAll('input[id], select[id]')).filter(
            input => input.id && !this.filterComponents.has(input.id)
        );

        // Create components for standard inputs using factory
        const components = FilterComponentFactory.createComponents(inputs);

        // Also detect compound widgets (DateRangePicker containers) inside the filter form
        const dateRangePickers = Array.from(
            filterForm.querySelectorAll('.k-daterangepicker[id]')
        ).filter(el => el.id && !this.filterComponents.has(el.id));

        dateRangePickers.forEach(container => {
            try {
                const adapter = AdapterFactory.createDateRangePickerAdapter(container);
                const component = new FilterComponent(container, adapter);
                components.push(component);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(`FilterManager: Skipping DateRangePicker ${container.id}:`, error.message);
            }
        });

        if (components.length === 0) {
            return;
        }

        // Add to our collection
        components.forEach(component => {
            if (component && component.id) {
                this.filterComponents.set(component.id, component);
            }
        });

        // Update pending adapters
        FilterComponentFactory.updatePendingComponents(Array.from(this.filterComponents.values()));

        // Initialize ready components (saved values will be loaded after pending adapters are ready)
        this.initializeComponents();
    }

    /**
     * Initialize all ready components
     */
    initializeComponents() {
        this.filterComponents.forEach(component => {
            if (!component.isInitialized) {
                try {
                    // Initialize component - pending bindings will be stored if widget not ready yet
                    component.initializeAutoFilter(this.handleFilterChange);
                    component.isInitialized = true;
                } catch (error) {
                    logError(error, { componentId: component.id });
                }
            }
        });

        // Finalize initialization after a short delay to ensure Kendo widgets are ready
        setTimeout(() => this.finalizeInitialization(), this.config.retryDelay);

        this.updateClearButtonState();
    }

    /**
     * Finalize initialization - update pending adapters and load saved values
     */
    finalizeInitialization() {
        if (this.isDestroyed) return;

        // Update any pending adapters (widgets that weren't ready during initial detection)
        const pendingComponents = Array.from(this.filterComponents.values())
            .filter(c => c.adapter && !c.adapter.isAvailable());

        if (pendingComponents.length > 0) {
            FilterComponentFactory.updatePendingComponents(pendingComponents);
        }

        // Load saved filter values
        this.loadSavedFilterValues();

        // Trigger first grid read only if GridPersistence hasn't already done so.
        // GridPersistence sets _persistenceQueried=true when it restores state and
        // triggers query() with the saved page/sort. In that case, another read
        // would be redundant (and could cause a race condition).
        const grid = $(this.config.gridSelector).data('kendoGrid');
        if (grid && grid.dataSource) {
            if (grid._persistenceQueried) {
                console.warn('[FilterManager] Skipping initial read - GridPersistence already restored state (page=' + grid.dataSource.page() + ')');
                delete grid._persistenceQueried;
            } else {
                console.warn('[FilterManager] Triggering initial read (no persistence state)');
                grid.dataSource.read();
            }
        }
    }

    /**
     * Handle filter change event
     */
    handleFilterChange() {
        if (this.isDestroyed) return;

        this.updateClearButtonState();

        // Save filter values to session storage
        this.saveFilterValuesToSession();

        this.handleGridRefresh(); // Refresh grid when filter changes

        // Call custom onFilter callback if provided
        if (typeof this.config.onFilter === 'function') {
            this.config.onFilter();
        }

        eventBus.emit(EVENTS.FILTER_CHANGED, {
            manager: this,
            componentCount: this.filterComponents.size
        });
    }

    /**
     * Handle clear filters event
     */
    handleClearFilters() {
        if (this.isDestroyed) return;

        try {
            // Clear all filter components (using proper Kendo widget methods)
            this.filterComponents.forEach(component => {
                try {
                    component.clearValue();
                } catch (error) {
                    logError(error, { componentId: component.id });
                }
            });

            // Update clear button state
            this.updateClearButtonState();

            // Clear saved filter values from session storage
            this.clearSavedFilterValues();

            // Emit event
            eventBus.emit(EVENTS.FILTER_CLEARED, { manager: this });

            // Refresh grid
            this.handleGridRefresh();

            // Call custom onFilter callback if provided
            if (typeof this.config.onFilter === 'function') {
                this.config.onFilter();
            }
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Handle grid refresh
     */
    handleGridRefresh() {
        if (this.isDestroyed) return;

        try {
            const grid = $(this.config.gridSelector).data('kendoGrid');
            if (grid) {
                const ds = grid.dataSource;
                if (ds) {
                    // Reset to page 1 when filter is applied - avoids empty page when
                    // user was on page 2+ and filter narrows results (e.g. 100 -> 10)
                    if (typeof ds.page === 'function') {
                        if (ds.page() !== 1) {
                            ds.page(1); // Triggers read, single request
                        } else {
                            ds.read(); // Already on page 1, filter changed
                        }
                    } else {
                        ds.read();
                    }
                }
                eventBus.emit(EVENTS.GRID_REFRESHED, {
                    gridSelector: this.config.gridSelector,
                    manager: this
                });
            }
            // Update clear button state after grid refresh
            this.updateClearButtonState();
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Check if any filters are active
     */
    hasActiveFilters() {
        for (const component of this.filterComponents.values()) {
            if (component.hasValue()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Update clear button state
     */
    updateClearButtonState() {
        if (this.isDestroyed) return;

        try {
            const clearBtn = $(this.config.clearButtonSelector);
            if (clearBtn.length) {
                if (this.hasActiveFilters()) {
                    clearBtn.show();
                } else {
                    clearBtn.hide();
                }
            }
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Generate filter values object for grid data source
     */
    /**
     * Generate filter values object for grid data source (server format)
     * Arrays are expanded to indexed format for ASP.NET Core model binding
     */
    generateFilterValues() {
        const rawValues = this.generateRawFilterValues();
        const expandedValues = this.expandCompoundValues(rawValues);
        return this.expandArraysForServer(expandedValues);
    }

    /**
     * Generate raw filter values (original format, for session storage)
     */
    generateRawFilterValues() {
        const filterValues = {};

        this.filterComponents.forEach(component => {
            if (component.id) {
                try {
                    const value = component.getValue();

                    // Only include non-empty values
                    if (this.isValueActive(value)) {
                        filterValues[component.id] = value;
                    }
                } catch (error) {
                    logError(error, { componentId: component.id });
                }
            }
        });

        return filterValues;
    }

    /**
     * Expand arrays to indexed format for ASP.NET Core model binding
     * e.g., {SeasonIds: [1, 2]} becomes {'SeasonIds[0]': 1, 'SeasonIds[1]': 2}
     */
    expandArraysForServer(values) {
        const expanded = {};

        Object.keys(values).forEach(key => {
            const value = values[key];
            if (Array.isArray(value)) {
                value.forEach((item, index) => {
                    expanded[`${key}[${index}]`] = item;
                });
            } else {
                expanded[key] = value;
            }
        });

        return expanded;
    }

    /**
     * Expand compound component values (e.g., DateRangePicker) into individual filter entries.
     * Compound components store their value as an object under the component ID,
     * but the server needs individual fields (e.g., PeriodFrom, PeriodTo).
     */
    expandCompoundValues(values) {
        const expanded = { ...values };

        this.filterComponents.forEach(component => {
            if (component.isCompound && expanded[component.id] !== undefined) {
                // Remove the compound object entry
                delete expanded[component.id];

                // Add individual entries from getFilterEntries()
                const entries = component.getFilterEntries();
                entries.forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        expanded[key] = value;
                    }
                });
            }
        });

        return expanded;
    }

    /**
     * Check if a value is considered active for filtering
     */
    isValueActive(value) {
        if (value === null || value === undefined || value === '') {
            return false;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === 'object') {
            return Object.keys(value).length > 0;
        }

        return true;
    }

    /**
     * Get all filter component values
     */
    getAllFilterValues() {
        const values = {};
        this.filterComponents.forEach((component, id) => {
            try {
                values[id] = component.getValue();
            } catch (error) {
                logError(error, { componentId: id });
                values[id] = null;
            }
        });
        return values;
    }

    /**
     * Get filter components by type
     */
    getComponentsByType(type) {
        const components = [];
        this.filterComponents.forEach(component => {
            if (component.constructor.name.toLowerCase().includes(type.toLowerCase())) {
                components.push(component);
            }
        });
        return components;
    }

    /**
     * Register this instance globally for getFilterValues function
     */
    registerGlobally() {
        // Clear previous instance
        if (window._activeFilterManager) {
            window._activeFilterManager.destroy();
        }

        window._activeFilterManager = this;
    }

    /**
     * Handle initialization errors
     */
    handleInitializationError(error) {
        logError(error);
        eventBus.emit(EVENTS.FILTER_MANAGER_ERROR, {
            error: error.message,
            manager: this
        });
    }

    /**
     * Utility method for delays
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Save current filter values to session storage
     */
    saveFilterValuesToSession() {
        if (!this.config.enableSessionStorage || !FilterStorage.isAvailable()) {
            return;
        }

        try {
            // Save raw format (with arrays) for proper restoration to widgets
            const filterValues = this.generateRawFilterValues();

            // Include expanded compound field entries for the session storage fallback path.
            // When the grid reads data before FilterManager is initialized (e.g., page refresh),
            // getFilterValues() loads raw values from session storage and only expands arrays.
            // Without these extra entries, compound values like PeriodRange are not decomposed
            // into PeriodFrom/PeriodTo and the server-side filter is silently skipped.
            this.filterComponents.forEach(component => {
                if (component.isCompound && filterValues[component.id] !== undefined) {
                    const entries = component.getFilterEntries();
                    entries.forEach(([key, value]) => {
                        if (value !== null && value !== undefined) {
                            filterValues[key] = value;
                        }
                    });
                }
            });

            const success = FilterStorage.save(filterValues);

            if (!success) {
                logWarning('Failed to save filter values to session storage');
            }
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Load saved filter values from session storage and apply them
     */
    loadSavedFilterValues() {
        if (!this.config.enableSessionStorage || !FilterStorage.isAvailable()) {
            return;
        }

        try {
            const savedValues = FilterStorage.load();
            if (ObjectUtils.isEmpty(savedValues)) {
                return;
            }

            // Apply values directly to form elements (handles standard inputs/selects)
            const valuesAppliedCount = FormUtils.applyValuesToForm(savedValues);

            // Apply compound component values (e.g., DateRangePicker)
            let compoundAppliedCount = 0;
            this.filterComponents.forEach(component => {
                if (component.isCompound && savedValues[component.id] !== undefined) {
                    try {
                        component.setValue(savedValues[component.id]);
                        compoundAppliedCount++;
                    } catch (error) {
                        logWarning(`Failed to restore compound filter ${component.id}:`, error);
                    }
                }
            });

            const totalApplied = valuesAppliedCount + compoundAppliedCount;
            if (totalApplied > 0) {
                this.updateClearButtonState();

                // Emit event that filters were loaded
                eventBus.emit(EVENTS.FILTER_LOADED, {
                    manager: this,
                    appliedCount: totalApplied,
                    savedValues: savedValues
                });
            }
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Clear saved filter values from session storage
     */
    clearSavedFilterValues() {
        if (!this.config.enableSessionStorage || !FilterStorage.isAvailable()) {
            return;
        }

        try {
            const success = FilterStorage.remove();
            if (!success) {
                logWarning('Failed to clear saved filter values from session storage');
            }
        } catch (error) {
            logError(error);
        }
    }

    /**
     * Cleanup method to clean up resources
     */
    destroy() {
        if (this.isDestroyed) return;

        try {
            // Clear global reference
            if (window._activeFilterManager === this) {
                window._activeFilterManager = null;
            }

            // Clear components
            this.filterComponents.clear();

            // Mark as destroyed
            this.isDestroyed = true;
            this.isInitialized = false;
        } catch (error) {
            logError(error);
        }
    }
}

/**
 * Default export - export the class directly
 */
export default FilterManager;
